"use client"

import { useState, useCallback, useEffect, useRef } from "react"
import { motion } from "framer-motion"
import { ArrowUp, Shuffle } from "lucide-react"

import { useGenerationStore } from "@/features/generation/store"
import { brand } from "@/config/brand"
import { ROUTES } from "@/shared/lib/constants"
import { useUIStore } from "@/shared/stores/ui-store"
import { cn } from "@/shared/lib/utils"
import { useLocalizedRouter } from "@/shared/hooks/use-localized-router"

const PLACEHOLDERS = [
  "Describe the image you want",
  "Write a scene to generate",
  "Create a new image",
]

const RANDOM_PROMPTS = [
  "Photorealistic intimate portrait, confident adult woman, cinematic bedroom light, soft skin detail, direct gaze, editorial composition",
  "Realistic adult glamour scene, warm window light, elegant lingerie styling, moody shadows, high-end photography, natural skin texture",
  "Anime-inspired adult fantasy portrait, expressive eyes, soft glow, sensual pose, detailed hair, cinematic composition",
  "Photorealistic couple scene, private luxury room, romantic tension, soft highlights, tasteful adult editorial mood",
  "Realistic boudoir portrait, black silk sheets, warm lamp light, confident expression, shallow depth of field, premium photo quality",
  "Anime adult muse, dramatic backlight, delicate linework, polished skin highlights, intimate atmosphere, rich color grading",
]

const TYPING_SPEED = 60
const LUCKY_TYPING_SPEED = 8
const ERASING_SPEED = 30
const PAUSE_AFTER_TYPING = 2000
const PAUSE_AFTER_ERASING = 400

function useTypingPlaceholder(texts: string[]) {
  const [display, setDisplay] = useState("")
  const indexRef = useRef(0)
  const charRef = useRef(0)
  const phaseRef = useRef<"typing" | "pausing" | "erasing">("typing")

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout>

    function tick() {
      const currentText = texts[indexRef.current]
      const phase = phaseRef.current

      if (phase === "typing") {
        charRef.current++
        setDisplay(currentText.slice(0, charRef.current))
        if (charRef.current >= currentText.length) {
          phaseRef.current = "pausing"
          timer = setTimeout(tick, PAUSE_AFTER_TYPING)
        } else {
          timer = setTimeout(tick, TYPING_SPEED)
        }
      } else if (phase === "pausing") {
        phaseRef.current = "erasing"
        timer = setTimeout(tick, ERASING_SPEED)
      } else {
        charRef.current--
        setDisplay(currentText.slice(0, charRef.current))
        if (charRef.current <= 0) {
          phaseRef.current = "typing"
          indexRef.current = (indexRef.current + 1) % texts.length
          timer = setTimeout(tick, PAUSE_AFTER_ERASING)
        } else {
          timer = setTimeout(tick, ERASING_SPEED)
        }
      }
    }

    timer = setTimeout(tick, PAUSE_AFTER_ERASING)
    return () => clearTimeout(timer)
  }, [texts])

  return display
}

function useTypingEffect(
  text: string,
  speed: number,
  onTyping: (partial: string) => void
) {
  const charRef = useRef(0)
  const targetRef = useRef(text)

  useEffect(() => {
    if (!text) return
    targetRef.current = text
    charRef.current = 0

    const timer = setInterval(() => {
      charRef.current++
      onTyping(targetRef.current.slice(0, charRef.current))
      if (charRef.current >= targetRef.current.length) {
        clearInterval(timer)
      }
    }, speed)

    return () => clearInterval(timer)
  }, [text, speed, onTyping])
}

export function FloatingGenerateBar() {
  const router = useLocalizedRouter()
  const [prompt, setPrompt] = useState("")
  const [typingTarget, setTypingTarget] = useState<string | null>(null)
  const [shuffleCount, setShuffleCount] = useState(0)
  const [isFocused, setIsFocused] = useState(false)
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const sidebarCollapsed = useUIStore((s) => s.sidebarCollapsed)

  const setStorePrompt = useGenerationStore((s) => s.setPrompt)
  const placeholderText = useTypingPlaceholder(PLACEHOLDERS)
  const canSubmit = prompt.trim().length >= 2

  const handleTypingUpdate = useCallback((partial: string) => {
    setPrompt(partial)
  }, [])

  useTypingEffect(typingTarget ?? "", LUCKY_TYPING_SPEED, handleTypingUpdate)

  const autoResize = useCallback(() => {
    const el = textareaRef.current
    if (!el) return
    el.style.height = "auto"
    el.style.height = Math.min(el.scrollHeight, 135) + "px"
  }, [])

  useEffect(() => {
    autoResize()
  }, [prompt, autoResize])

  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault()
      const trimmed = prompt.trim()
      if (trimmed.length < 2) return
      setStorePrompt(trimmed)
      router.push(ROUTES.generate)
    },
    [prompt, router, setStorePrompt]
  )

  const handleShufflePrompt = useCallback(() => {
    const availablePrompts = RANDOM_PROMPTS.filter(
      (randomPrompt) => randomPrompt !== typingTarget
    )
    const prompts = availablePrompts.length > 0 ? availablePrompts : RANDOM_PROMPTS
    const nextPrompt = prompts[Math.floor(Math.random() * prompts.length)]
    setTypingTarget(nextPrompt)
    setShuffleCount((count) => count + 1)
    textareaRef.current?.focus()
  }, [typingTarget])

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      setTypingTarget(null)
      setPrompt(e.target.value.slice(0, 500))
    },
    []
  )

  return (
    <div
      className={cn(
        "fixed bottom-0 left-0 right-0 z-30 pb-4 pt-6 lg:pb-6",
        sidebarCollapsed ? "lg:left-[72px]" : "lg:left-[260px]"
      )}
      style={{
        paddingBottom: "max(1rem, env(safe-area-inset-bottom, 0px))",
      }}
    >
      <motion.form
        onSubmit={handleSubmit}
        className="relative mx-auto flex w-[680px] max-w-[calc(100vw-2rem)] items-center gap-3 rounded-[22px] border border-white/[0.08] bg-card/95 px-3 py-3 backdrop-blur-xl transition-colors"
        initial={{ opacity: 0, y: 18, scale: 0.98 }}
        animate={{
          opacity: 1,
          y: 0,
          scale: isFocused ? 1.012 : 1,
          borderColor: isFocused
            ? brand.borders.hairlineDarkStrong
            : brand.borders.hairlineDark,
          boxShadow: isFocused ? "0 18px 60px rgba(0, 0, 0, 0.38)" : "none",
        }}
        transition={{
          type: "spring",
          ...brand.motion.spring.standard,
        }}
      >
        <motion.button
          type="button"
          onClick={handleShufflePrompt}
          className="flex size-10 shrink-0 items-center justify-center rounded-full bg-white/[0.08] text-white/70 transition-colors hover:bg-white/[0.12] hover:text-white focus:outline-none focus:ring-2 focus:ring-accent-blue/60"
          whileHover={{ scale: 1.08 }}
          whileTap={{ scale: 1.18 }}
          transition={{
            type: "spring",
            ...brand.motion.spring.tight,
          }}
          aria-label="Shuffle prompt"
          title="Shuffle prompt"
        >
          <motion.span
            key={shuffleCount}
            className="flex"
            initial={{ rotate: 0, scale: 1 }}
            animate={{ rotate: shuffleCount ? 180 : 0, scale: [1, 1.22, 1] }}
            transition={{ duration: 0.42, ease: brand.motion.easing.standard }}
          >
            <Shuffle className="size-4" />
          </motion.span>
        </motion.button>

        <textarea
          ref={textareaRef}
          rows={1}
          value={prompt}
          onChange={handleInputChange}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey && prompt.trim()) {
              e.preventDefault()
              handleSubmit(e)
            }
          }}
          placeholder={placeholderText}
          className="min-h-10 flex-1 resize-none border-none bg-transparent py-2 text-base font-normal leading-relaxed text-white placeholder:text-white/45 focus:outline-none focus:ring-0"
          style={{
            minHeight: "40px",
            maxHeight: "135px",
            overflowY: "auto",
            scrollbarWidth: "none",
            transition: "height 200ms ease",
          }}
          aria-label="Image generation prompt"
        />

        <motion.button
          type="submit"
          disabled={!canSubmit}
          className="flex size-10 shrink-0 items-center justify-center rounded-full text-sm font-semibold transition-colors"
          whileHover={canSubmit ? { scale: 1.08 } : undefined}
          whileTap={canSubmit ? { scale: 1.16 } : undefined}
          transition={{
            type: "spring",
            ...brand.motion.spring.tight,
          }}
          style={{
            backgroundColor: canSubmit ? "white" : "rgba(255,255,255,0.08)",
            color: canSubmit ? "black" : "rgba(255,255,255,0.36)",
            cursor: canSubmit ? "pointer" : "not-allowed",
          }}
          aria-label="Generate image"
        >
          <ArrowUp className="size-5" />
        </motion.button>
      </motion.form>
    </div>
  )
}
